import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

const SIZE_API = 'https://outgrow-production.up.railway.app/api/public/size-finder';

const SizeFinder = ({ brand, brandDisplay }) => {
  const [unit, setUnit] = useState('metric');
  const [height, setHeight] = useState('');
  const [weight, setWeight] = useState('');
  const [footLength, setFootLength] = useState('');
  const [category, setCategory] = useState('tops');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const isShoes = category === 'shoes';

  useEffect(() => {
    const locale = navigator.language || 'en-US';
    if (['en-US', 'en-GB', 'en-CA'].some(l => locale.startsWith(l))) setUnit('imperial');
  }, []);

  useEffect(() => { setResult(null); setError(''); }, [category]);

  const handleUnitChange = (newUnit) => {
    if (newUnit === unit) return;
    if (height) setHeight(newUnit === 'metric' ? Math.round(parseFloat(height) * 2.54).toString() : (Math.round((parseFloat(height) / 2.54) * 10) / 10).toString());
    if (weight) setWeight(newUnit === 'metric' ? (Math.round(parseFloat(weight) * 0.453592 * 10) / 10).toString() : Math.round(parseFloat(weight) / 0.453592).toString());
    if (footLength) setFootLength(newUnit === 'metric' ? (Math.round(parseFloat(footLength) * 2.54 * 10) / 10).toString() : (Math.round((parseFloat(footLength) / 2.54) * 10) / 10).toString());
    setUnit(newUnit);
    setResult(null);
  };

  const findSize = async () => {
    if (isShoes) {
      if (!parseFloat(footLength)) { setError('Please enter a valid foot length'); return; }
    } else {
      if (!parseFloat(height)) { setError('Please enter a valid height'); return; }
    }
    setError(''); setLoading(true); setResult(null);

    const apiCategory = category === 'fullbody' ? 'tops' : category;
    const body = { brand, category: apiCategory, gender: 'boy' };

    if (isShoes) {
      body.foot_length_cm = Math.round((unit === 'imperial' ? parseFloat(footLength) * 2.54 : parseFloat(footLength)) * 10) / 10;
    } else {
      body.height_cm = Math.round(unit === 'imperial' ? parseFloat(height) * 2.54 : parseFloat(height));
      if (weight) body.weight_kg = Math.round((unit === 'imperial' ? parseFloat(weight) * 0.453592 : parseFloat(weight)) * 10) / 10;
    }

    try {
      const res = await fetch(SIZE_API, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      if (!res.ok) throw new Error('API request failed');
      const data = await res.json();

      const cleanSize = (s) => s ? s.replace(/\b(boys?|girls?|slim|plus|husky|petite?)\b/gi, '').replace(/\(\s*\)/g, '').replace(/\s+/g, ' ').trim() : s;
      let recSize = cleanSize(data.recommended_size);
      let nextSize = cleanSize(data.next_size);
      if (nextSize === recSize) nextSize = null;

      if (data.longevity_months != null && data.longevity_months < 0) {
        setResult({ noMatch: true });
      } else if (data.longevity_months != null && data.longevity_months < 3 && nextSize) {
        setResult({ ...data, recommended_size: nextSize, next_size: null, longevity_months: data.next_size_details?.longevity_months ?? null, sizedUp: true });
      } else if (data.longevity_months != null && data.longevity_months < 3 && !nextSize) {
        setResult({ noMatch: true });
      } else {
        setResult({ ...data, recommended_size: recSize, next_size: nextSize });
      }
    } catch (err) {
      console.error('Size finder error:', err);
      setResult({ noMatch: true, apiError: true });
    } finally {
      setLoading(false);
    }
  };

  const placeholder = (type) => {
    if (type === 'height') return unit === 'metric' ? '98' : '39';
    if (type === 'foot') return unit === 'metric' ? '18' : '7';
    return unit === 'metric' ? '15' : '33';
  };

  const categories = [
    { id: 'tops', label: 'Tops', icon: '/images/tops_icon.png' },
    { id: 'bottoms', label: 'Bottoms', icon: '/images/bottoms_icon.png' },
    { id: 'shoes', label: 'Shoes', icon: '/images/shoes_icon.png' },
    { id: 'fullbody', label: 'Full Body', icon: '/images/fullbody_icon.png' },
  ];

  return (
    <div className="sf-widget" data-testid="size-finder-widget">
      <div className="sf-header">
        <div className="sf-header-left">
          <img src="/ruler-icon.png" alt="" className="sf-header-icon" />
          <div>
            <div className="sf-header-title">Kids Size Finder</div>
            <div className="sf-header-sub">Free size finder</div>
          </div>
        </div>
        <span className="sf-header-badge">Free</span>
      </div>

      <div className="sf-body">
        <div className="sf-top-row">
          <h3 className="sf-find-title">Find your child's {brandDisplay} {isShoes ? 'shoe ' : ''}size</h3>
          <div className="sf-unit-toggle">
            <button onClick={() => handleUnitChange('metric')} className={`sf-unit-btn ${unit === 'metric' ? 'active' : ''}`}>
              {isShoes ? 'cm' : 'cm / kg'}
            </button>
            <button onClick={() => handleUnitChange('imperial')} className={`sf-unit-btn ${unit === 'imperial' ? 'active' : ''}`}>
              {isShoes ? 'in' : 'in / lbs'}
            </button>
          </div>
        </div>

        <p className="sf-instructions">
          {isShoes ? "Enter your child's foot length and we'll show the right shoe size." : "Enter your child's measurements and we'll show the right size — plus how long it will last."}
        </p>

        {isShoes ? (
          <div className="sf-inputs">
            <div className="sf-input-group">
              <label className="sf-label">Foot Length</label>
              <div className="sf-input-wrap">
                <input type="number" step="0.1" value={footLength} onChange={(e) => { setFootLength(e.target.value); setError(''); }} placeholder={placeholder('foot')} className={`sf-input ${error ? 'sf-input-error' : ''}`} data-testid="sf-foot-input" />
                <span className="sf-input-unit">{unit === 'metric' ? 'cm' : 'in'}</span>
              </div>
              {error && <p className="sf-error">{error}</p>}
              <p className="sf-hint">Measure from heel to longest toe while standing</p>
            </div>
          </div>
        ) : (
          <div className="sf-inputs sf-inputs-grid">
            <div className="sf-input-group">
              <label className="sf-label">Height</label>
              <div className="sf-input-wrap">
                <input type="number" value={height} onChange={(e) => { setHeight(e.target.value); setError(''); }} placeholder={placeholder('height')} className={`sf-input ${error ? 'sf-input-error' : ''}`} data-testid="sf-height-input" />
                <span className="sf-input-unit">{unit === 'metric' ? 'cm' : 'in'}</span>
              </div>
              {error && <p className="sf-error">{error}</p>}
            </div>
            <div className="sf-input-group">
              <label className="sf-label">Weight <span className="sf-label-opt">(optional)</span></label>
              <div className="sf-input-wrap">
                <input type="number" value={weight} onChange={(e) => setWeight(e.target.value)} placeholder={placeholder('weight')} className="sf-input" data-testid="sf-weight-input" />
                <span className="sf-input-unit">{unit === 'metric' ? 'kg' : 'lbs'}</span>
              </div>
            </div>
          </div>
        )}

        <div className="sf-categories">
          <label className="sf-label">Category</label>
          <div className="sf-cat-grid">
            {categories.map((cat) => (
              <button key={cat.id} onClick={() => setCategory(cat.id)} className={`sf-cat-btn ${category === cat.id ? 'active' : ''}`} data-testid={`sf-cat-${cat.id}`}>
                <img src={cat.icon} alt={cat.label} className="sf-cat-icon-img" />
                <span className="sf-cat-label">{cat.label}</span>
              </button>
            ))}
          </div>
        </div>

        <button onClick={findSize} disabled={loading} className="sf-submit" data-testid="sf-submit-btn">
          {loading ? 'Finding size...' : "Find My Child's Size →"}
        </button>

        {result && result.noMatch && (
          <div className="sf-result sf-result-nomatch" data-testid="sf-result-nomatch">
            <div className="sf-result-title">No matching size found</div>
            <p className="sf-result-text">This brand doesn't have a fitting size for your child's measurements at the selected category. Try a different category or check another brand.</p>
          </div>
        )}

        {result && !result.noMatch && (
          <div className="sf-result sf-result-match" data-testid="sf-result-match">
            <div className="sf-result-main">
              <div>
                <div className="sf-result-label">Recommended {brandDisplay} {isShoes ? 'shoe ' : ''}size</div>
                <div className="sf-result-size">{result.recommended_size}</div>
                {result.sizedUp && <div className="sf-result-sizedup">Sized up for room to grow</div>}
                {result.age_range && <div className="sf-result-age">{result.age_range}</div>}
              </div>
              {result.longevity_months && (
                <div className="sf-longevity">
                  <div className="sf-longevity-num">{result.longevity_months}</div>
                  <div className="sf-longevity-label">months of<br />wear left</div>
                </div>
              )}
            </div>

            {isShoes && (result.us_size || result.eu_size || result.uk_size) && (
              <div className="sf-shoe-sizes">
                {result.us_size && <div className="sf-shoe-size"><span className="sf-shoe-region">US</span><span className="sf-shoe-val">{result.us_size}</span></div>}
                {result.eu_size && <div className="sf-shoe-size"><span className="sf-shoe-region">EU</span><span className="sf-shoe-val">{result.eu_size}</span></div>}
                {result.uk_size && <div className="sf-shoe-size"><span className="sf-shoe-region">UK</span><span className="sf-shoe-val">{result.uk_size}</span></div>}
              </div>
            )}

            {result.fit_note && (
              <div className="sf-fitnote"><strong>Fit note:</strong> {result.fit_note}</div>
            )}

            {result.next_size && (
              <div className="sf-nextsize">Next size up: <strong>{result.next_size}</strong>{result.limiting_factor && ` (based on ${result.limiting_factor})`}</div>
            )}

            <div className="sf-app-cta">
              <p className="sf-app-cta-title">Get precise results with chest & waist measurements</p>
              <p className="sf-app-cta-sub">Download the app for the full experience — free, no subscription.</p>
              <div className="sf-app-badges">
                <a href="https://apps.apple.com/us/app/kids-size-finder/id6763076022" target="_blank" rel="noopener noreferrer"><img src="/badge-appstore.png" alt="App Store" className="sf-badge-img" /></a>
                <a href="https://play.google.com/store/apps/details?id=com.outgrow.kidssizefinder" target="_blank" rel="noopener noreferrer"><img src="/badge-googleplay.png" alt="Google Play" className="sf-badge-img" /></a>
              </div>
            </div>
          </div>
        )}
      </div>

      <p className="sf-disclaimer">Size recommendations are based on official brand size charts and are provided as a guide only. Fit may vary by item, style, and individual child.</p>
    </div>
  );
};

export default SizeFinder;
